/**
 * ViewToggle Component
 * Connected button group toggle for switching between views.
 */

import styled, { css } from 'styled-components';

export interface ViewToggleOption {
  key: string;
  label: string;
}

export interface ViewToggleProps {
  options: ViewToggleOption[];
  activeKey: string;
  onChange: (key: string) => void;
}

const ButtonGroup = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const ToggleButton = styled.button<{ $isActive: boolean; $isFirst: boolean; $isLast: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border: none;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  /* Divider between buttons */
  &:not(:first-child) {
    border-left: 1px solid ${({ theme }) => theme.colors.borders.medium};
  }

  ${({ $isActive, theme }) =>
    $isActive
      ? css`
          background: ${theme.colors.primary.base};
          color: ${theme.colors.text.white};
        `
      : css`
          background: ${theme.colors.backgrounds.white};
          color: ${theme.colors.text.secondary};

          &:hover {
            background: ${theme.colors.backgrounds.light};
            color: ${theme.colors.text.primary};
          }
        `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: -2px;
    z-index: 1;
    position: relative;
  }

  /* Remove default radius, shape controlled by group */
  border-radius: 0;

  ${({ $isFirst, theme }) =>
    $isFirst &&
    css`
      border-radius: ${theme.borderRadius.md} 0 0 ${theme.borderRadius.md};
    `}

  ${({ $isLast, theme }) =>
    $isLast &&
    css`
      border-radius: 0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0;
    `}
`;

export function ViewToggle({ options, activeKey, onChange }: ViewToggleProps) {
  return (
    <ButtonGroup role="group" aria-label="View toggle">
      {options.map((option, index) => (
        <ToggleButton
          key={option.key}
          $isActive={option.key === activeKey}
          $isFirst={index === 0}
          $isLast={index === options.length - 1}
          onClick={() => onChange(option.key)}
          aria-pressed={option.key === activeKey}
          type="button"
        >
          {option.label}
        </ToggleButton>
      ))}
    </ButtonGroup>
  );
}

export default ViewToggle;
